import { useState, useEffect, useCallback } from "react";
import moment from "moment";
import AxiosInstance, { TokenService } from "../../../../services/Api";

/**
 * Build the `status` payload based on the active tab.
 *
 *  All tab   ("" | undefined | null) → ""          (empty string, no filter)
 *  Submitted (1)                     → [1, 4]      (submitted + resubmitted)
 *  Approved  (2)                     → [2, 5]      (approved + completed)
 *  Rejected  (3)                     → [3]
 *  Resubmitted (4)                   → [4]
 *  Completed (5)                     → [5]
 */
const buildStatusPayload = (tabStatus) => {
  // All tab → send "" (not an array)
  if (tabStatus === "" || tabStatus === undefined || tabStatus === null) {
    return "";
  }
  const num = Number(tabStatus);
  if (Number.isNaN(num)) return "";
  // Submitted tab also includes Resubmitted
  if (num === 1) return [1, 4];
  // Approved tab also includes Completed
  if (num === 2) return [2, 5];
  // Every other tab → single-item array
  return [num];
};

/**
 * @param tabStatus           Active tab status (see buildStatusPayload)
 * @param weekFlag            1 = this week only, 0 = all
 * @param academicIdOverride  When set, overrides the logged-in academic year
 *                            (used by the "Previous Years" tab dropdown)
 * @param enabled             When false, no API call is made (default true).
 *                            Lets the Previous Years tab wait until a year is picked.
 */
export default function useLessonPlanData(
  tabStatus = "",
  weekFlag = 0,
  academicIdOverride = "",
  enabled = true,
) {
  const user = TokenService.getUser();
  const clientData = user?.client_data?.[0] || {};
  const school_id = clientData?.school_id || "";
  const academic_id = clientData?.academic_year_id || "";
  // const staffId =
  //   clientData.master_id != null && clientData.master_id !== ""
  //     ? Number(clientData.master_id)
  //     : null;
  const staff_Id =
    user?.id != null && user?.id !== "" ? Number(user?.id) : null;

  // Use the override when provided, otherwise fall back to the logged-in year.
  const effectiveAcademicId =
    academicIdOverride !== undefined &&
    academicIdOverride !== null &&
    academicIdOverride !== ""
      ? academicIdOverride
      : academic_id;

  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [rowCount, setRowCount] = useState(0);
  const [columnFilters, setColumnFilters] = useState([]);
  const [pagination, setPagination] = useState({ pageIndex: 0, pageSize: 10 });

  const [topicOptions, setTopicOptions] = useState([]);
  const [subTopicOptions, setSubTopicOptions] = useState([]);

  // read a single filter value from the columnFilters array
  const getFilterValue = (id) => {
    const f = columnFilters.find((x) => x.id === id);
    const v = f?.value;
    return v === undefined || v === null ? "" : v;
  };

  // date range filter (scheduled_date) - expected as [startDate, endDate]
  const scheduledDateRange = getFilterValue("scheduled_date");
  const startDate =
    Array.isArray(scheduledDateRange) && scheduledDateRange[0]
      ? moment(scheduledDateRange[0]).format("YYYY-MM-DD")
      : "";
  const endDate =
    Array.isArray(scheduledDateRange) && scheduledDateRange[1]
      ? moment(scheduledDateRange[1]).format("YYYY-MM-DD")
      : "";

  const fetchData = useCallback(async () => {
    // Skip the call entirely until the caller is ready (e.g. no academic year picked yet)
    if (!enabled) {
      setData([]);
      setRowCount(0);
      setLoading(false);
      return;
    }

    setLoading(true);
    try {
      const payload = {
        school_id,
        staff_id: staff_Id,
        class_id: String(getFilterValue("class_code") || ""),
        subject: String(getFilterValue("book") || ""),
        period: String(getFilterValue("periods") || ""),
        day: String(getFilterValue("dayids") || ""),
        // "" for All tab, array for every other tab
        status: buildStatusPayload(tabStatus),
        section: String(getFilterValue("section") || ""),
        week: weekFlag ? 1 : 0,
        topic: String(getFilterValue("topic") || ""),
        sub_topic: String(getFilterValue("sub_topic") || ""),
        medium: String(getFilterValue("medium") || ""),
        status_filter: String(getFilterValue("status_text") || ""),
        // 👇 NEW filters used by the Previous Years tab
        created_by: String(getFilterValue("created_by") || ""),
        approved_by: String(getFilterValue("approved_by") || ""),
        search: "",
        page: pagination.pageIndex + 1,
        limit: pagination.pageSize,
        startDate: startDate || "",
        endDate: endDate || "",
        // overridable academic year (Previous Years dropdown)
        academic_id: effectiveAcademicId,
      };

      const response = await AxiosInstance.post(
        `/book/lessonPlan/list`,
        payload,
      );

      if (response?.data?.status) {
        const rawList = response.data.data || [];

        const transformed = rawList.map((item, index) => {
          const transList = item.trans || [];

          // Derive "Created By" / "Approved By" names. Prefer explicit
          // fields from the API if present, otherwise pull from the
          // transaction history.
          const nameForStatus = (statuses, actions) => {
            const entry = transList.find(
              (tr) =>
                statuses.includes(Number(tr.status)) ||
                actions.includes(String(tr.action || "").toLowerCase()),
            );
            return entry?.action_by_name || "";
          };

          const created_by =
            item.created_by_name ||
            item.created_by_user ||
            nameForStatus([1], ["submitted", "resubmitted"]) ||
            "";

          const approved_by =
            item.approved_by_name ||
            item.approved_by_user ||
            nameForStatus([2], ["approved"]) ||
            "";

          return {
            id: item.id,
            def_id: item.def_id,
            item_id: item.item_id,
            s_no: pagination.pageIndex * pagination.pageSize + index + 1,
            periods: item.periods,
            dayid: item.dayid,
            // column mappings (new response -> table columns)
            book: item.subject_name || "",
            topic: item.topic || "",
            sub_topic: item.sub_topic || "",
            class_code: item.class_name || "",
            section: item.section_ids || "",
            medium: item.medium || "",
            scheduled_date: item.schedule_date || "",
            status: item.status, // numeric
            status_text: item.status_label || "", // "Submitted" | "Approved" | ...
            // 👇 NEW: carry generate_count through so the All table can
            //         enable/disable the per-row "Add" button.
            generate_count:
              item.generate_count != null ? Number(item.generate_count) : 0,
            // staff_id (handy for the create/add navigation)
            staff_id: item.staff_id ?? null,
            // 👇 NEW columns for Previous Years tab
            created_by,
            approved_by,
            // pass the original transactions array through for the history panel
            history: transList.map((tr) => ({
              user_name: tr.action_by_name || "-",
              topic: tr.topic || "-",
              sub_topic: tr.sub_topic || "-",
              status: tr.status,
              status_text: tr.action || "",
              updated_on: tr.created_at,
              comments: tr.comments,
            })),
            // keep the raw item in case the view page needs it
            _raw: item,
          };
        });

        setData(transformed);
        setRowCount(response.data.pagination?.total || transformed.length);

        // populate dropdown options (unique, non-empty)
        const topics = [
          ...new Set(
            rawList
              .map((i) => i.topic)
              .filter((t) => t && String(t).trim() !== ""),
          ),
        ];
        const subs = [
          ...new Set(
            rawList
              .map((i) => i.sub_topic)
              .filter((t) => t && String(t).trim() !== ""),
          ),
        ];
        setTopicOptions(topics);
        setSubTopicOptions(subs);
      } else {
        setData([]);
        setRowCount(0);
      }
    } catch (e) {
      console.error("Lesson plan list fetch error:", e);
      setData([]);
      setRowCount(0);
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    pagination.pageIndex,
    pagination.pageSize,
    columnFilters,
    weekFlag,
    tabStatus,
    academicIdOverride,
    enabled,
  ]);

  // reset to page 0 whenever a filter / year / tab changes
  useEffect(() => {
    setPagination((prev) => ({ ...prev, pageIndex: 0 }));
  }, [columnFilters, weekFlag, tabStatus, academicIdOverride]);

  // fetch on every relevant change
  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleColumnFiltersChange = (updaterOrValue) => {
    setColumnFilters((prev) =>
      typeof updaterOrValue === "function"
        ? updaterOrValue(prev)
        : updaterOrValue,
    );
  };

  return {
    data,
    loading,
    rowCount,
    pagination,
    setPagination,
    columnFilters,
    setColumnFilters,
    handleColumnFiltersChange,
    topicOptions,
    subTopicOptions,
    refetch: fetchData,
  };
}